import type { RouteLocationNormalized, Router } from "vue-router";
// Relative (not @/) imports: reachable from the ./auth expose, whose emitted
// d.ts ships to consumers where the producer's @/ alias can't resolve.
import { useAuthStore } from "../../shared/auth/auth.store";
import { ensureRemoteRoutesForPath } from "../../shared/federation/remote-registry";

/**
 * Attaches the host's navigation guard to ANY router (this app's or a remote
 * dev harness's). Instance-agnostic: the auth store resolves from the active
 * pinia and remote routes load into the router passed in.
 *
 * Consumer contract: the router must define routes named "login" and
 * "not-found" — the guard redirects to them.
 */
export function installAuthGuard(router: Router): void {
  router.beforeEach(async (to: RouteLocationNormalized) => {
    const remoteRouteLoadStatus = await ensureRemoteRoutesForPath(to.path, router);

    if (remoteRouteLoadStatus === "failed") {
      return { name: "not-found", query: { from: to.fullPath } };
    }

    if (to.matched.length === 0 && remoteRouteLoadStatus === "loaded-now") {
      return {
        path: to.path,
        query: to.query,
        hash: to.hash,
        replace: true,
      };
    }

    if (to.matched.length === 0) {
      return { name: "not-found" };
    }

    // A permission requirement implies an authentication requirement.
    const requiredPermission = to.meta?.requiresPermission as string | undefined;
    const requiresAuth = to.meta?.requiresAuth !== false || !!requiredPermission;

    if (requiresAuth) {
      const authStore = useAuthStore();

      if (!authStore.isInitialized) {
        await authStore.initialize();
      }

      if (!authStore.isAuthenticated) {
        return { name: "login" };
      }

      if (requiredPermission && !authStore.can("manage", requiredPermission)) {
        return { name: "not-found", query: { from: to.fullPath, reason: "forbidden" } };
      }
    }

    return true;
  });
}
